import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
// QA-127 #1 / QA-120: the SAME line-art icon the Gallery header's Capture
// entry point uses (components/WheelGallery/Header/CaptureNavEntry.jsx) —
// swapped in for DonutWheelIcon, a "wheel" glyph that reads as an "Add to
// Wheel" affordance, not the Capture feature, on the ONE surface that is
// supposed to BE the Capture feature's own header.
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Tooltip from "@mui/material/Tooltip";

import TextView from "../../../components/TextView";
import { colors } from "../../../utils";
import { getWheelDisplayName } from "../../../utils/helpers";
import {
  CAPTURE_ENABLED,
  CAPTURE_SEGMENT_DROP_ENABLED,
} from "../../../constants/featureFlags";
import { openModal } from "../../../redux/reducers/dialog";
import {
  captureTypeLabel,
  detachConfirmCopy,
  filterCaptures,
  flattenCapturePages,
  isRetryBusy,
  FILTER_ALL,
} from "../Capture.helpers";
import {
  DRAWER_SCOPE,
  drawerEmptyCopy,
  isAttachedToWheel,
  scopeCount,
} from "./captureDrawerScopes";
import CaptureDrawerScopeTabs from "./CaptureDrawerScopeTabs";
import CaptureDrawerTypeTabs from "./CaptureDrawerTypeTabs";
import {
  WHEEL_CAPTURE_STATE,
  wheelCaptureState,
  wheelCaptureCount,
} from "./CapturedOnWheel.helpers";
import {
  useWheelCaptureItems,
  useCaptureItems,
  useDetachCaptureItem,
} from "../../../services/capture";
// SHARED capture card styleds (composed, never hand-rolled — G-21).
import {
  List,
  IconBtn,
  StateWrap,
  EmptyArt,
  SkeletonCard,
  SkeletonBox,
  SearchWrap,
  SearchInput,
} from "../styles";
import AttachedCaptureRow from "./AttachedCaptureRow";
import SegmentPickerDialog from "./SegmentPickerDialog";
import { hasSegments } from "../segmentPicker.helpers";
import useCaptureDrawerPushMode from "./useCaptureDrawerPushMode";
import {
  Backdrop,
  Drawer,
  DrawerHeader,
  HeaderText,
  DrawerTitle,
  DrawerControls,
  DrawerBody,
  RetryButton,
  LoadMoreButton,
} from "./CapturedOnWheel.styles";

const TITLE_ID = "captured-on-wheel-title";

const LoadingState = () => (
  <List aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <SkeletonCard key={i}>
        <SkeletonBox style={{ width: 56, height: 56, flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <SkeletonBox style={{ width: "40%", height: 12 }} />
          <SkeletonBox style={{ width: "85%", height: 12 }} />
        </div>
      </SkeletonCard>
    ))}
  </List>
);

/**
 * "Captured items on this wheel" — wheel-side read-back surface (ISP-4068).
 *
 * A right-rail drawer (the app's wheel-side panel pattern; deck concept 03) that
 * reads back the captures the owner attached to THIS wheel and renders them with
 * the deck's loading / empty / error states. Owner-only + flag-gated: mount it
 * only for the wheel owner and only when CAPTURE_ENABLED; it also self-gates on
 * the flag here as a belt-and-suspenders guard, so an accidental mount renders
 * nothing while the flag is OFF.
 *
 * Props:
 *   isOpen    whether the drawer is shown
 *   onClose   called on backdrop click / Escape / close button
 *   wheelId   the wheel whose attached captures to read
 *   wheelName optional wheel name for the header subtitle
 *   embedded  ISP-9999: renders as a plain in-flow block (no fixed position,
 *             no backdrop, no modal semantics) filling whatever container
 *             hosts it, instead of a viewport-fixed overlay. Set by the
 *             builder (BuilderCapturedOnWheel), which now mounts this inside
 *             the same side-pane slot every other rail pane uses. Defaults to
 *             false so any other/standalone usage — and this file's own test
 *             suite — is byte-identical to before.
 */
const CapturedOnWheel = ({
  isOpen = false,
  onClose,
  wheelId,
  wheelName,
  embedded = false,
  // CAPTURE_SEGMENT_DROP. `wheelSections` is the wheel's Segment list
  // (`wheel_section`); `onInsertIntoSegment(item, segmentId)` performs the
  // insert and RESOLVES to { ok, message } so this drawer can report a failure
  // instead of closing as if it worked. Both default to inert, so the flag-OFF
  // render is byte-identical to today.
  wheelSections = [],
  onInsertIntoSegment = null,
  // ISP-9999 add-to-segment-autocreate. Present only when the mount point can
  // actually create a Segment (currently the builder). `null` preserves the
  // pre-existing "no Segments yet" dead-end messaging for any mount point
  // that cannot (e.g. a future read-only surface) rather than guessing.
  onAutoCreateSegment = null,
}) => {
  const closeRef = useRef(null);
  const drawerRef = useRef(null);
  const dispatch = useDispatch();

  // Push mode: the page shrinks BESIDE this drawer and stays live, so the
  // drawer is not modal — no scrim (handled in the stylesheet) and, just as
  // importantly, no `aria-modal` claim that the rest of the page is gone.
  const isPushMode = useCaptureDrawerPushMode();

  // The "Add to Segment" picker: which capture is being placed, and how the
  // last attempt went.
  const [pickerItem, setPickerItem] = useState(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const [placeError, setPlaceError] = useState(null);
  // ISP-9999 add-to-segment-autocreate: which row's auto-create is in flight,
  // so that ONE row disables while its Segment is being created rather than
  // the whole list — and so a double-click can't create two Segments.
  const [autoCreatingId, setAutoCreatingId] = useState(null);

  const canAddToSegment = CAPTURE_SEGMENT_DROP_ENABLED && !!onInsertIntoSegment;
  const canAutoCreateSegment =
    CAPTURE_SEGMENT_DROP_ENABLED && !!onAutoCreateSegment;
  const wheelHasSegments = hasSegments(wheelSections);

  /**
   * A wheel with zero Segments has nothing for the picker to list — Kelly's
   * dialog design deliberately calls that a dead end (SegmentPickerDialog's
   * EMPTY state). Rather than show that dead end, create the Segment the
   * capture obviously belongs in and skip the picker entirely: there is no
   * choice to make when there is exactly one possible destination.
   */
  const handleAutoCreate = async (item) => {
    if (!onAutoCreateSegment) return;
    setAutoCreatingId(item.id);
    try {
      const result = await onAutoCreateSegment(item);
      if (result && result.ok === false) {
        toast.error(result.message || "Couldn't add this to the Segment.");
        return;
      }
      toast.success("Created a new Segment and added this to it.");
    } catch (err) {
      toast.error("Couldn't add this to the Segment. Please try again.");
    } finally {
      setAutoCreatingId(null);
    }
  };

  const openPicker = (item) => {
    setPlaceError(null);
    if (!wheelHasSegments && canAutoCreateSegment) {
      handleAutoCreate(item);
      return;
    }
    setPickerItem(item);
  };

  const closePicker = () => {
    setPickerItem(null);
    setPlaceError(null);
  };

  /**
   * Place the capture into every chosen Segment (QA-1096).
   *
   * `segmentIds` is placed ONE AT A TIME, in order, each fully awaited before
   * the next starts — `onInsertIntoSegment` (the builder's `place()`) may need
   * to switch the single shared craft.js canvas to a different Segment before
   * it can insert, and firing the next placement before that switch has landed
   * would race the canvas the same way a single-segment double-add would.
   *
   * A FAILURE keeps the dialog open with its reason, rather than closing and
   * leaving the user to work out that nothing happened — silence after an
   * explicit action is the exact defect this feature exists to remove. That
   * only holds when NOTHING succeeded, though: once at least one Segment has
   * really been written to, re-submitting the same selection on "retry" would
   * insert the capture into that Segment a SECOND time, so a partial result
   * reports what happened (success count + failure reason) and closes rather
   * than inviting a re-click that duplicates already-placed items.
   */
  const handlePlace = async (segmentIds) => {
    if (!pickerItem || !onInsertIntoSegment || !segmentIds || !segmentIds.length) return;
    setIsPlacing(true);
    setPlaceError(null);
    let succeeded = 0;
    let firstFailureMessage = null;
    for (const segmentId of segmentIds) {
      try {
        // eslint-disable-next-line no-await-in-loop -- must serialize: see doc comment above.
        const result = await onInsertIntoSegment(pickerItem, segmentId);
        if (result && result.ok === false) {
          firstFailureMessage = firstFailureMessage || result.message || "Couldn't add this to the Segment.";
        } else {
          succeeded += 1;
        }
      } catch (err) {
        firstFailureMessage = firstFailureMessage || "Couldn't add this to the Segment. Please try again.";
      }
    }
    setIsPlacing(false);

    if (!firstFailureMessage) {
      toast.success(
        segmentIds.length > 1 ? `Added to ${segmentIds.length} Segments.` : "Added to the Segment."
      );
      closePicker();
      return;
    }
    if (succeeded > 0) {
      toast.success(`Added to ${succeeded} of ${segmentIds.length} Segments.`);
      toast.error(firstFailureMessage);
      closePicker();
      return;
    }
    setPlaceError(firstFailureMessage);
  };

  /**
   * QA-77 — the pane has TWO sections: the captures assigned to this wheel, and
   * the owner's whole Capture library (which includes them). They are two
   * different server reads, so they are two queries, both fired while the
   * drawer is open: a section badge that only learns its total once the section
   * is opened would print "…" on the very control the user needs the number on.
   *
   * The library read is the SAME react-query key the Capture inbox uses, so
   * opening the pane after visiting Capture is served from cache rather than
   * re-fetching.
   */
  const [scope, setScope] = useState(DRAWER_SCOPE.WHEEL);
  const [typeFilter, setTypeFilter] = useState(FILTER_ALL);
  // QA-127 #3: a search bar, like the other library tabs (pages/Capture's own
  // toolbar) already have. Same `filterCaptures` helper, so "search" means
  // the exact same thing here as it does on the full inbox page.
  const [searchQuery, setSearchQuery] = useState("");

  const wheelQuery = useWheelCaptureItems(wheelId, {
    // Fire only while the drawer is open (and the flag is on / a wheel exists).
    enabled: CAPTURE_ENABLED && isOpen && !!wheelId,
  });
  const libraryQuery = useCaptureItems({
    enabled: CAPTURE_ENABLED && isOpen,
  });

  const query = scope === DRAWER_SCOPE.LIBRARY ? libraryQuery : wheelQuery;
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = query;

  // Keyed to THIS wheel so the confirmed detach prunes this drawer's list (and
  // its header count) optimistically, and rolls back if the request fails.
  const detach = useDetachCaptureItem(wheelId);

  /**
   * Detach one capture from this wheel. Uses the app's shared confirm dialog
   * (redux openModal + the <DeleteModal /> already mounted by the builder page
   * that renders this drawer) rather than a new bespoke dialog, with copy that
   * says what detaching actually does — the capture returns to the inbox, it is
   * not destroyed.
   */
  const handleDetach = (item) => {
    dispatch(
      openModal({
        ...detachConfirmCopy(wheelName ? getWheelDisplayName(wheelName) : ""),
        onConfirm: () => {
          detach.mutate(item.id, {
            onSuccess: () => toast.success("Capture detached."),
            onError: () =>
              toast.error("Could not detach. Please try again."),
          });
        },
      })
    );
  };

  const scopeItems = useMemo(() => flattenCapturePages(data), [data]);
  // The media-type row and the search box both filter what the ACTIVE section
  // shows; neither changes which section is active, so all three controls
  // stay independent.
  const items = useMemo(
    () => filterCaptures(scopeItems, { type: typeFilter, query: searchQuery }),
    [scopeItems, typeFilter, searchQuery]
  );
  const itemCount = wheelCaptureCount(items);
  const state = wheelCaptureState({ isLoading, isError, itemCount });
  const retryBusy = isRetryBusy(query);

  // Section badges (QA-77 "each option should display the total number").
  const wheelItems = useMemo(() => flattenCapturePages(wheelQuery.data), [wheelQuery.data]);
  const libraryItems = useMemo(() => flattenCapturePages(libraryQuery.data), [libraryQuery.data]);
  const scopeCounts = useMemo(
    () => ({
      [DRAWER_SCOPE.WHEEL]: scopeCount(wheelQuery.data, wheelItems),
      [DRAWER_SCOPE.LIBRARY]: scopeCount(libraryQuery.data, libraryItems),
    }),
    [wheelQuery.data, wheelItems, libraryQuery.data, libraryItems]
  );

  // Switching section must reset a type filter (and search) that may not
  // match anything in the other section — otherwise the pane shows an empty
  // list under a tab/search the user cannot see is still active.
  const handleScope = (next) => {
    setScope(next);
    setTypeFilter(FILTER_ALL);
    setSearchQuery("");
  };

  const emptyCopy = drawerEmptyCopy({
    scope,
    typeFilter,
    typeLabel: typeFilter === FILTER_ALL ? "" : captureTypeLabel(typeFilter),
    hasQuery: !!searchQuery.trim(),
  });

  // Escape closes; move focus to the close button when the drawer opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  // The closed drawer stays MOUNTED and slid off-screen (transform) so it can
  // animate, and it is marked aria-hidden. But aria-hidden over a still-focusable
  // control is the axe `aria-hidden-focus` violation: a keyboard user tabs onto a
  // Close button (and, once items load, every card) that is invisible and absent
  // from the accessibility tree — focus appears to vanish. MEASURED on web-stg
  // 2026-07-28, /builder/3844 at 1600x1000: the closed drawer sat at x=1600 with
  // aria-hidden="true", inert=false, and one tabbable descendant.
  //
  // Set imperatively rather than as a JSX prop: `inert` is not a prop React 18
  // recognises, and styled-components v6 filters unknown props off DOM nodes —
  // toggling the attribute on the node itself is the one path neither layer can
  // silently drop.
  useEffect(() => {
    const node = drawerRef.current;
    if (!node) return;
    node.toggleAttribute("inert", !isOpen);
  }, [isOpen]);

  // Belt-and-suspenders: never render the surface while the flag is OFF.
  if (!CAPTURE_ENABLED) return null;

  return (
    <>
      {!embedded && (
        <Backdrop $open={isOpen} onClick={onClose} data-testid="captured-on-wheel-backdrop" />
      )}
      <Drawer
        ref={drawerRef}
        $open={isOpen}
        $embedded={embedded}
        role="dialog"
        aria-modal={embedded || isPushMode ? undefined : "true"}
        aria-labelledby={TITLE_ID}
        aria-hidden={!isOpen}
        data-push-mode={isPushMode ? "true" : "false"}
        data-embedded={embedded ? "true" : "false"}
        data-testid="captured-on-wheel-drawer"
      >
        <DrawerHeader>
          <EditNoteOutlinedIcon
            style={{ color: colors.PrimaryPurple700, fontSize: 22 }}
          />
          <HeaderText>
            {/* QA-127b: header simplified to match the other side-pane
                section headers — drop the wheel-name/item-count subtitle. */}
            <DrawerTitle id={TITLE_ID}>Capture</DrawerTitle>
          </HeaderText>
          {/* ISP-9999 (fix-tooltip-stays-open-after-click-capture-surfaces,
              MEASURED web-stg /builder/5399 at 820, 2026-09-09): opening this
              drawer programmatically focuses the Close button a few lines up
              (for keyboard users), and a default MUI Tooltip treats that focus
              exactly like a hover — popping "Close" open and pinning it over
              the Search field below, since nothing ever blurs the button
              afterward. disableFocusListener stops that auto-focus from
              opening the tooltip while leaving real mouse hover working. */}
          <Tooltip title="Close" disableFocusListener>
            <IconBtn
              ref={closeRef}
              type="button"
              aria-label="Close captured items"
              onClick={onClose}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconBtn>
          </Tooltip>
        </DrawerHeader>

        {/* QA-77 — section tabs, then the media-type filter row. QA-127 #3 adds
            a search box above both, matching the other library tabs (the
            Capture inbox page's own toolbar). All three sit ABOVE the list and
            outside the scrolling body so they stay reachable. */}
        <DrawerControls data-testid="capture-drawer-controls">
          <SearchWrap>
            <SearchRoundedIcon fontSize="small" />
            <SearchInput
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              aria-label="Search captures"
            />
          </SearchWrap>
          <CaptureDrawerScopeTabs
            scope={scope}
            onScope={handleScope}
            counts={scopeCounts}
          />
          <CaptureDrawerTypeTabs
            items={scopeItems}
            type={typeFilter}
            onType={setTypeFilter}
          />
        </DrawerControls>

        <DrawerBody
          id="captured-on-wheel-body"
          data-testid="captured-on-wheel-body"
          data-state={state}
          data-scope={scope}
          role="tabpanel"
          aria-labelledby={`capture-scope-tab-${scope}`}
        >
          {state === WHEEL_CAPTURE_STATE.LOADING && <LoadingState />}

          {state === WHEEL_CAPTURE_STATE.ERROR && (
            <StateWrap role="alert">
              <EmptyArt>
                <ErrorOutlineRoundedIcon />
              </EmptyArt>
              <TextView variant="subtitle" color={colors.black}>
                Couldn&apos;t load captured items
              </TextView>
              <TextView variant="text" color={colors.textGrey}>
                Something went wrong reading this wheel&apos;s captures.
              </TextView>
              <RetryButton type="button" onClick={() => refetch?.()} disabled={retryBusy}>
                {retryBusy ? "Retrying…" : "Retry"}
              </RetryButton>
            </StateWrap>
          )}

          {state === WHEEL_CAPTURE_STATE.EMPTY && (
            <StateWrap>
              <EmptyArt>
                <InboxRoundedIcon />
              </EmptyArt>
              {/* The empty state has to name the section the user is actually
                  looking at — telling someone their wheel has no captures while
                  they are on the Full Library tab is a wrong answer, and telling
                  them their library is empty while a type filter is hiding rows
                  is worse. */}
              <TextView variant="subtitle" color={colors.black}>
                {emptyCopy.title}
              </TextView>
              <TextView variant="text" color={colors.textGrey}>
                {emptyCopy.body}
              </TextView>
            </StateWrap>
          )}

          {state === WHEEL_CAPTURE_STATE.POPULATED && (
            <>
              <List as="ul" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {items.map((item) => (
                  <AttachedCaptureRow
                    key={item.id}
                    item={item}
                    onDetach={handleDetach}
                    isDetaching={detach.isLoading}
                    canAddToSegment={canAddToSegment}
                    onAddToSegment={openPicker}
                    wheelHasSegments={wheelHasSegments}
                    canAutoCreateSegment={canAutoCreateSegment}
                    isAutoCreating={autoCreatingId === item.id}
                    /* The wheel section holds only attached rows; the library
                       section holds both, so the row is told which it is rather
                       than assuming. */
                    isAttachedHere={
                      scope === DRAWER_SCOPE.WHEEL || isAttachedToWheel(item, wheelId)
                    }
                  />
                ))}
              </List>
              {hasNextPage && (
                <LoadMoreButton
                  type="button"
                  onClick={() => fetchNextPage?.()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Loading…" : "Load more"}
                </LoadMoreButton>
              )}
            </>
          )}
        </DrawerBody>
      </Drawer>

      {/* CAPTURE_SEGMENT_DROP — the picker. Kept OUTSIDE <Drawer> because the
          drawer carries `inert` while closed and is a transformed container; a
          modal nested inside it would inherit both. */}
      {canAddToSegment && (
        <SegmentPickerDialog
          isOpen={!!pickerItem}
          onClose={closePicker}
          onConfirm={handlePlace}
          wheelSections={wheelSections}
          captureLabel={pickerItem ? captureTypeLabel(pickerItem.type).toLowerCase() : "capture"}
          isBusy={isPlacing}
          errorMessage={placeError}
        />
      )}
    </>
  );
};

export default CapturedOnWheel;
